import { Grid } from "@mui/material";
import { useState } from "react";
import { useForm } from "../../hooks/general/useForm";
import { useTagGroup } from "../../hooks/tags/queries/useTagGroup";
import {
  reportFields,
  reportStrings as strings,
} from "../../resources/strings/reports";
import { ReportFilters } from "../../types/documents/Report";
import { Button } from "../general/Button";
import { BaseControl } from "../general/controls/BaseControl";
import { DatePicker } from "../general/controls/DatePicker";
import { Dropdown } from "../general/controls/Dropdown";
import { ReportFieldSelectorModal } from "./ReportFieldSelectorModal";

interface Props {
  filters: ReportFilters;
  handleFilterChange: (name: keyof ReportFilters, value: any) => void;
  clearFilters: () => void;
  selectedFields: (keyof typeof reportFields)[];
  setSelectedFields: (fields: (keyof typeof reportFields)[]) => void;
}
type Component = (props: Props) => JSX.Element;

export const ReportFilterBar: Component = ({
  filters,
  handleFilterChange,
  clearFilters,
  selectedFields,
  setSelectedFields,
}: Props) => {
  const [currentFilters, handleChange] = useForm(filters);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch project options
  const [projectOptions] = useTagGroup([], "projects");

  // Add a default option for "All Projects"
  const projectDropdownOptions = [
    { label: strings.labels.allProjects, value: "" },
    ...projectOptions,
  ];

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    handleFilterChange("start", currentFilters.start);
    handleFilterChange("end", currentFilters.end);
    handleFilterChange("project", currentFilters.project);
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <Grid container alignItems="center" spacing={2} role="searchbox">
          {/* Project Dropdown */}
          <Grid item xs={3}>
            <BaseControl control={false}>
              <Dropdown
                config={{
                  name: "project",
                  label: strings.labels.project,
                  value: currentFilters.project ?? "",
                  options: projectDropdownOptions,
                }}
                handleChange={handleChange}
              />
            </BaseControl>
          </Grid>

          {/* Start Date */}
          <Grid item xs>
            <BaseControl control={false}>
              <DatePicker
                config={{
                  name: "start",
                  label: strings.header.start,
                  value: currentFilters.start ?? "",
                  outputFormatter: (date) => date.toISODate(),
                }}
                handleChange={handleChange}
              />
            </BaseControl>
          </Grid>

          {/* End Date */}
          <Grid item xs>
            <BaseControl control={false}>
              <DatePicker
                config={{
                  name: "end",
                  label: strings.header.end,
                  value: currentFilters.end ?? "",
                  outputFormatter: (date) => date.toISODate(),
                }}
                handleChange={handleChange}
              />
            </BaseControl>
          </Grid>

          {/* Search Button */}
          <Grid item xs={2}>
            <BaseControl control={false}>
              <Button type="submit" label={strings.labels.search} />
            </BaseControl>
          </Grid>

          {/* Reset Button */}
          <Grid item xs={2}>
            <BaseControl control={false}>
              <Button
                label={strings.labels.reset}
                variant="outlined"
                onClick={clearFilters}
              />
            </BaseControl>
          </Grid>

          {/* Configure Fields Button */}
          <Grid item xs={2}>
            <BaseControl control={false}>
              <Button
                label={strings.labels.configureFields}
                variant="outlined"
                onClick={() => setIsModalOpen(true)}
              />
            </BaseControl>
          </Grid>
        </Grid>
      </form>

      {/* Report Field Selector Modal */}
      <ReportFieldSelectorModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedFields={selectedFields}
        setSelectedFields={setSelectedFields}
      />
    </>
  );
};
